import { useState } from "react";
import { useField } from "formik";

import { useGetDealShortlist } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import {
  SearchAutocomplete,
  type SearchItem,
} from "@vega-core/components/Form/Controls/Input/SearchAutocomplete/SearchAutocomplete";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";

import {
  MIN_SEARCH_QUERY_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
} from "../../constants/searchConstants";

type LpSelectorProps = {
  dealId: string;
  disabled?: boolean;
  label?: string;
  name: string;
  placeholder?: string;
};

type LpSearchItem = SearchItem & {
  lp: DealShortlistItemResponse;
};

export const LpSelector = ({
  dealId,
  disabled = false,
  label,
  name,
  placeholder,
}: LpSelectorProps) => {
  const [field, , helpers] = useField(name);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY_MS);

  const isSearching = debouncedSearchTerm.length >= MIN_SEARCH_QUERY_LENGTH;

  const { data: shortlistResponse, isLoading } = useGetDealShortlist(
    dealId,
    undefined,
    {
      query: {
        enabled: !!dealId,
        select: (data) => data.data?.dealShortlistItems || [],
      },
    },
  );

  const filteredProspects =
    shortlistResponse?.filter((prospect) => {
      if (!isSearching) return true;

      return prospect.limitedPartner.name
        .toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase());
    }) || [];

  const searchItems = filteredProspects.map((prospect) => ({
    content: prospect.limitedPartner.name,
    id: prospect.limitedPartner.id,
    lp: prospect,
    searchValue: prospect.limitedPartner.name,
  }));

  const handleItemSelect = (item: LpSearchItem | null) => {
    helpers.setValue(item?.lp.limitedPartner.id ?? "");
  };

  return (
    <div className="space-y-1">
      {label ? <FormLabel forName={name} label={label} /> : null}
      <SearchAutocomplete
        className="[&>div]:px-0 [&>div>svg:first-child]:left-3 [&>div>svg:last-child]:right-3"
        disabled={disabled}
        emptyMessage={
          isSearching
            ? m.formFields_dropdown_noOptions()
            : m.placeholders_search()
        }
        isLoading={isLoading}
        items={searchItems}
        name={field.name}
        onChange={setSearchTerm}
        onItemSelect={handleItemSelect}
        placeholder={placeholder || m.core_select_an_option()}
        showAllOnFocus
      />
    </div>
  );
};
